import { getJSON } from '@snapbrains/snaputils/src/ajax';
import { snapRequest } from '@snapbrains/snaputils/src/snap_request';

// Common sort order
export const bestMatchSort = 'Match+Score';
export const highestPriceSort = 'Retail+Price%7Cdesc';
export const lowestPriceSort = 'Retail+Price';
export const commissionSort = 'Commission';
export const discountSort = 'Price+Discount+Percent%7Cdesc';

const avantlinkUrl = 'https://classic.avantlink.com/api.php';

export function searchQuery(args) {
  const {
    query,
    page,
    sortOrder,
    merchantIds = [],
    category,
    subCategory,
    customCategory,
    brand,
    resultCount,
    overFetchCount,
    onSaleOnly,
    searchOnSaleOnly,
    onSaleLevel,
    minPrice,
    maxPrice,
    department,
    merchantCategoryIds,
    sortHighestCommissionFirst,
    avantlinkAffiliateId,
    avantlinkWebsiteId,
    searchUrl, // Can be passed to override the default avantlinkUrl. Useful if an app needs to proxy the AvantLink through a specific service or domain
  } = args;

  const searchResultsBase = ((page || 0) * resultCount);
  let url = `${searchUrl || avantlinkUrl}?affiliate_id=${avantlinkAffiliateId}`;
  url = `${url}&module=ProductSearch&output=json&website_id=${avantlinkWebsiteId}`;
  url = `${url}&search_results_count=${overFetchCount || resultCount}`;
  url = `${url}&search_term=${encodeURIComponent(query)}`;

  // A string with a custom category specification that just adds and additional search query
  if (customCategory) {
    url = `${url}+${encodeURIComponent(customCategory)}`;
  }

  url = `${url}&search_results_base=${searchResultsBase}`;

  if(merchantIds && merchantIds.length > 0) {
    url = `${url}&merchant_ids=${merchantIds.join('%7C')}`;
  }

  // Any field can be used for sort order. Common options are listed above
  if(sortOrder) {
    url = `${url}&search_results_sort_order=${sortOrder}`;
  } else if(sortHighestCommissionFirst) {
    url = `${url}&search_results_sort_order=${commissionSort}`;
  }

  if(onSaleOnly || searchOnSaleOnly) {
    url = `${url}&search_on_sale_only=1`;
  }

  if(onSaleLevel) {
    url = `${url}&search_on_sale_level=${onSaleLevel}`;
  }

  // NOTE these are not very useful. As far as I can tell they are only available by inspecting the
  // the dropdown menu in the AvantLink API builder and are limited to the following values:
  // '89' : 'Accessories',
  // '1' : 'Apparel',
  // '97' : 'Arts/Crafts',
  // '73' : 'Arts/Crafts &gt; Scrapbooking',
  // '157' : 'Automotive',
  // '190' : 'Beauty',
  // '188' : 'Books',
  // '149' : 'Business',
  // '31' : 'Cleaning Supplies',
  // '5' : 'Collectibles',
  // '24' : 'Cooking',
  // '26' : 'Custom/Personalized Products',
  // '57' : 'Cycling',
  // '121' : 'DOTD/ODAT',
  // '20' : 'Electronics',
  // '189' : 'Entertainment',
  // '45' : 'Eyewear',
  // '22' : 'Fashion',
  // '186' : 'Firearms/Tactical',
  // '93' : 'Fitness',
  // '161' : 'Flash Sale',
  // '6' : 'Food/Beverage',
  // '21' : 'Footwear',
  // '105' : 'Gifts/Flowers',
  // '14' : 'Health/Wellness',
  // '81' : 'Hobbies',
  // '25' : 'Home Furnishings',
  // '30' : 'Home/Garden',
  // '23' : 'Hunting/Fishing',
  // '125' : 'Hunting/Fishing/Firearms &gt; Self Defense',
  // '12' : 'Jewelry',
  // '19' : 'Marketing',
  // '9' : 'Medical Supplies',
  // '27' : 'Motor Sports',
  // '4' : 'Music/Video',
  // '181' : 'Non-profit/Charity',
  // '37' : 'Office Products',
  // '2' : 'Outdoor/Recreation',
  // '133' : 'Outdoor/Recreation &gt; Apparel',
  // '113' : 'Outdoor/Recreation &gt; Skateboarding',
  // '69' : 'Outdoor/Recreation &gt; Snowboarding',
  // '77' : 'Outdoor/Recreation &gt; Water Sports',
  // '3' : 'Pet Supplies',
  // '177' : 'Photography',
  // '187' : 'Preparedness',
  // '191' : 'Security',
  // '185' : 'Self Help',
  // '41' : 'Software',
  // '7' : 'Special Occasion',
  // '11' : 'Sports',
  // '173' : 'Sports &gt; Apparel',
  // '28' : 'Telecommunications',
  // '10' : 'Tools',
  // '137' : 'Toys',
  // '17' : 'Travel/Luggage',
  // '101' : 'Virtual Malls',
  if(merchantCategoryIds && merchantCategoryIds.length > 0) {
    url = `${url}&merchant_category_ids=${merchantCategoryIds.join('%7C')}`;
  }

  if(maxPrice) {
    url = `${url}&search_price_maximum=${maxPrice}`;
  }

  if(minPrice) {
    url = `${url}&search_price_minimum=${minPrice}`;
  }

  // Boolean, enables the use of advanced search syntax, causing special treatment in the following circumstances:
  // preceding a word with "+" will restrict the search to ONLY products that mention that word, preceding a
  // word with "-" will restrict the search to exclude any products that mention that word, and the keyword "
  // OR " can be used to perform multiple simultaneous searches (in place of OR you can use the pipe character "|"
  // to separate multiple search terms).
  url = `${url}&search_advanced_syntax=1`;

  // A product brand to which search results should be restricted (strict filter only; does not allow wildcards).
  if(brand) {
    url = `${url}&search_brand=${brand}`;
  }

  // A string category name to which search results should be restricted.
  if(category) {
    url = `${url}&search_category=${category}`;
  }

  // A string sub category name to which search results should be restricted.
  if (subCategory) {
    url = ''.concat(url, '&search_subcategory=').concat(subCategory);
  }

  // A department to which search results should be restricted.
  if(department) {
    url = `${url}&search_department=${department}`;
  }

  return url;
}

export function search(args, callback) {
  const url = searchQuery(args);
  const {
    searchHeaders = {}, // Optional headers that can be passed to the endpoint at searchUrl
  } = args;
  getJSON(url, (json) => {
    if (json && json[0] === 'No active merchant associations found for this affiliate, using the specified options.') {
      callback([]);
    } else {
      callback(json);
    }
  }, searchHeaders);
}

// async function runs a search using the given args.
// returns:
//  error: !response.ok,
//  status: response.status,
//  statusText: response.statusText
//  text,
//  json: JSON.parse(text),
export async function searchProducts(args) {
  const url = searchQuery(args);
  return snapRequest(url);
}
